import { PrimaryButton, Spinner, SpinnerSize } from "@fluentui/react";
import React, { useContext, useEffect, useState, useMemo } from "react";
import "./QuotationInvoice.css";
import { makePdf } from "../PrintQuotation/PrintQuotation";
import { useAppInsightsContext } from "@microsoft/applicationinsights-react-js";
import { QuotationInfoType } from "../Quotation.types";
import { QuotationContext } from "../QuotationContext";
import { CountryProfileService } from "../../../services/CountryProfiles/CountryProfileService";
import { useNavigate } from "react-router-dom";
import { priceWithCurrency, priceWithCurrencyForJapan, symbolForCurrency } from "../../../Utils/PriceWithCurrency";
import { CountryProfileInfo } from "../../CountriesProfile/CountriesProfile.type";
import { IExtendedDetailItemEntity } from "../Common/Interfaces/IItemEntity";
import { formattedPrice, formattedPriceForJapan } from "../../../Utils/FormattedPrice";
import { formatDateWithPattern, getCountryDateFormat } from "../../../Utils/DateFormatUtility";
import { getQuotePreviewLabels, getDefaultLanguage, getAvailableLanguages } from "../../../common/constants/QuoteTemplateLabels";
import { hasWarrantyItems } from "../../../Utils/QuotationUtils";

// Countries that may have domestic reverse charges applied
const DOMESTIC_REVERSE_CHARGE_COUNTRIES = ["Germany", "Austria", "Netherlands", "Denmark", "Spain", "Italy", "Portugal", "Sweden"];

// Helper function for country-specific date formatting
const formatDateForCountry = async (dateStr: string | undefined, countryName: string | undefined): Promise<string> => {
  if (!dateStr) return '';
  
  try {
    const date = new Date(dateStr);
    if (isNaN(date.getTime())) {
      console.warn(`Invalid date string: ${dateStr}`);
      return dateStr || '';
    }
    
    let dateFormat = 'DD/MM/YYYY'; // Default format
    if (countryName) {
      const countryFormat = await getCountryDateFormat(countryName);
      if (countryFormat) {
        dateFormat = countryFormat;
      }
    }
    
    return formatDateWithPattern(date, dateFormat);
  } catch (error) {
    console.error('Error formatting date:', error);
    return dateStr || '';
  }
};

interface GenericQuotationTemplateProps {
  country: string;
  selectedLanguage?: string;
}

export const GenericQuotationTemplate: React.FunctionComponent<GenericQuotationTemplateProps> = ({ 
  country,
  selectedLanguage
}) => {
  const navigate = useNavigate();
  const appInsights = useAppInsightsContext();
  const [termsAndConditionsDocument, settermsAndConditionsDocument] = useState(new Uint8Array());
  const [countryProfileInfo, setCountryProfileInfo] = useState<CountryProfileInfo>();
  const [termsAndConditionsDocumentId, settermsAndConditionsDocumentId] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const { quotationInfo } = useContext(QuotationContext) as QuotationInfoType;
  
  // Language state management with country-specific defaults
  // Read from localStorage first to get the most up-to-date value set by QuotationDetail
  const [currentLanguage, setCurrentLanguage] = useState<string>(() => {
    const storedLanguage = localStorage.getItem("selectedLanguage");
    return storedLanguage || selectedLanguage || getDefaultLanguage(country);
  });
  
  // Sync currentLanguage with selectedLanguage prop when it changes
  // Also check localStorage for the most up-to-date value
  // This fixes the issue where the language switcher selection wasn't being applied
  // when first opening Preview Quote from the main search page
  useEffect(() => {
    const storedLanguage = localStorage.getItem("selectedLanguage");
    const languageToUse = storedLanguage || selectedLanguage;
    if (languageToUse) {
      setCurrentLanguage(languageToUse);
    }
  }, [selectedLanguage]);
  
  // Get available languages for this country
  const availableLanguages = getAvailableLanguages(country);
  
  // Get labels for the specific country and language (fr=French, en=English)
  const labels = getQuotePreviewLabels(country, currentLanguage);
  const customerPrimaryContactName =
    quotationInfo?.customer?.primaryContact?.trim() ||
    `${quotationInfo?.customer?.authorizedBuyerFirstName || ""} ${quotationInfo?.customer?.authorizedBuyerLastName || ""}`.trim() ||
    `${quotationInfo?.customer?.selectedBillToContact?.contactFirstName || ""} ${quotationInfo?.customer?.selectedBillToContact?.contactLastName || ""}`.trim() ||
    "";
  
  // Calculate number of columns for the table
  const columnsNumber = quotationInfo?.customer?.region === "EMEA" ? 9 : 8;
  
  // State for formatted dates
  const [formattedQuotationDate, setFormattedQuotationDate] = useState<string>('');
  const [formattedExpiryDate, setFormattedExpiryDate] = useState<string>('');
  const [formattedCurrentDate, setFormattedCurrentDate] = useState<string>('');
  
  // Format dates according to country's preferred date format
  useEffect(() => {
    const formatDates = async () => {
      try {
        if (quotationInfo?.quotationDate) {
          const formatted = await formatDateForCountry(quotationInfo.quotationDate, country);
          setFormattedQuotationDate(formatted);
        }
        
        if (quotationInfo?.expiryDate) {
          const formatted = await formatDateForCountry(quotationInfo.expiryDate, country);
          setFormattedExpiryDate(formatted);
        }
        
        // Format current date
        const currentFormatted = await formatDateForCountry(new Date().toISOString(), country);
        setFormattedCurrentDate(currentFormatted);
      } catch (error) {
        console.error('Error formatting dates:', error);
      }
    };

    formatDates();
  }, [quotationInfo?.quotationDate, quotationInfo?.expiryDate, country]);

  const pdfFileName = `${quotationInfo?.quotationNumber}_${country}_Quotation`;

  const lineItems: IExtendedDetailItemEntity[] =
    quotationInfo?.bundleItems?.length > 0
      ? ([...quotationInfo?.standaloneItems, ...quotationInfo?.bundleItems] as IExtendedDetailItemEntity[])
      : quotationInfo?.standaloneItems?.length > 0
        ? ([...quotationInfo?.standaloneItems, ...quotationInfo.bundleItems] as IExtendedDetailItemEntity[])
        : [];

  // Memoized domestic reverse charges logic
  const showDomesticReverseCharges = useMemo(() => {
    const isInDRCCountry = DOMESTIC_REVERSE_CHARGE_COUNTRIES.includes(country);
    const hasTaxExemptFalse = quotationInfo?.customer?.taxExempt === false;
    const hasValidVatId = quotationInfo?.customer?.taxId && quotationInfo?.customer?.taxId.trim() !== "";
    const isTotalTaxZero = Number(quotationInfo?.totalTax) === 0;
    const subtotal = quotationInfo?.subTotal !== undefined && quotationInfo?.subTotal !== null ? Number(quotationInfo.subTotal) : null;
    
    // Base conditions must be met
    if (!isInDRCCountry || !hasTaxExemptFalse || !hasValidVatId || subtotal === null) {
      return false;
    }
    
    // Check if any product contains specific keywords
    // Keywords ordered from most specific to least specific to avoid incorrect matches
    const surfaceKeywords = country === "Germany"
      ? ['xbox', 'surface pro x', 'surface pro', 'surface go', 'surface duo']
      : ['xbox', 'surface pro x', 'surface pro', 'surface go', 'surface laptop', 'surface book', 'surface duo'];
    
    // Helper function to check if a product matches any surface keyword
    const matchesSurfaceKeyword = (productName: string): boolean => {
      return surfaceKeywords.some(keyword => {
        const regex = new RegExp(`\\b${keyword}\\b`, 'i');
        return regex.test(productName);
      });
    };
    
    const hasQualifyingProduct = lineItems.some(item => {
      const productName = item.productTitle || '';
      const hasKeyword = matchesSurfaceKeyword(productName);
      const hasVat = item.lineItemTax && Number(item.lineItemTax) > 0;
      return hasKeyword && !hasVat;
    });
    
    // Check if any other product (non-surface keywords) has lineItemTax > 0
    const hasOtherProductWithVat = lineItems.some(item => {
      const productName = item.productTitle || '';
      const hasKeyword = matchesSurfaceKeyword(productName);
      const hasVat = item.lineItemTax && Number(item.lineItemTax) > 0;
      return !hasKeyword && !hasVat; // Other products (not in keywords) with VAT
    });
    
    // Country-specific rules
    switch (country) {
      case "Austria":
        // Austria: (surfaceKeywords OR other products with VAT) AND subtotal >= EUR 5000
        return (hasQualifyingProduct || hasOtherProductWithVat) && subtotal >= 5000;
      
      case "Germany":
        // Germany: show DRC note when VAT is effectively not applied OR qualifying products are present, and subtotal is at least EUR 5000
        return (isTotalTaxZero || hasQualifyingProduct) && subtotal >= 5000;
      
      case "Netherlands":
        // Netherlands: existing conditions AND subtotal >= EUR 10000
        return (isTotalTaxZero || hasQualifyingProduct) && subtotal >= 10000;
      
      case "Denmark":
        // Denmark: existing conditions (no subtotal threshold)
        return isTotalTaxZero || hasQualifyingProduct;
      
      case "Spain":
        // Spain: existing conditions AND subtotal >= EUR 10000
        return (isTotalTaxZero || hasQualifyingProduct) && subtotal >= 10000;
      
      case "Sweden":
        // Sweden: existing conditions AND subtotal >= SEK 100000
        return (isTotalTaxZero || hasQualifyingProduct) && subtotal >= 100000;
      
      case "Italy":
      case "Portugal":
        // Other DRC countries: existing conditions
        return isTotalTaxZero || hasQualifyingProduct;
      default:
        return false;
    }
  }, [country, quotationInfo, lineItems]);

  useEffect(() => {
    getCountryProfile();
  }, []);

  const getCountryProfile = async () => {
    setIsLoading(true);
    CountryProfileService.getCountryProfileByCountryName(quotationInfo?.customer?.country)
      .then(async (res) => {
        if (res !== undefined) {
          settermsAndConditionsDocumentId(
            res.data.data.data.termsAndConditions[
              res.data.data.data.termsAndConditions?.length - 1
            ]?.id
          );
          setCountryProfileInfo(res.data.data.data);
          appInsights.trackEvent({
            name: `In GenericQuotationTemplate.tsx, country detail fetched successfully for ${country}.`,
          });
          setIsLoading(false);
        }
      })
      .catch((error) => {
        setIsLoading(false);
        appInsights.trackException({
          error,
          properties: {
            name: `In GenericQuotationTemplate.tsx, error while getting country detail for ${country}.`,
          },
        });
      });
  };

  // Effect to trigger PDF generation when terms and conditions document is loaded
  useEffect(() => {
    async function PrintPdf() {
      try {
        if (termsAndConditionsDocument.byteLength > 0) {
          setIsLoading(true);
          await makePdf(
            appInsights,
            pdfFileName,
            lineItems.length,
            termsAndConditionsDocument
          );
          setIsLoading(false);
          settermsAndConditionsDocument(new Uint8Array());
        }
      } catch (error) {
        setIsLoading(false);
      }
    }
    PrintPdf();
  }, [termsAndConditionsDocument.byteLength]);

  const PrintHtmlToPdf = async () => {
    appInsights.trackEvent({
      name: `In GenericQuotationTemplate.tsx, Clicked Print button to download the ${country} Quote Preview`,
    });
    if (
      termsAndConditionsDocument.byteLength === 0 &&
      termsAndConditionsDocumentId
    ) {
      await CountryProfileService.getCountryProfileDocument(
        termsAndConditionsDocumentId
      ).then((res) => {
        appInsights.trackEvent({
          name: `In GenericQuotationTemplate.tsx, Country Terms and Condition document fetched successfully for ${country}.`,
        });
        if (res.data === null) {
          makePdf(appInsights, pdfFileName, lineItems.length, termsAndConditionsDocument);
        }
        settermsAndConditionsDocument(res.data);
      })
      .catch((error) => {
        makePdf(appInsights, pdfFileName, lineItems.length, termsAndConditionsDocument);
        appInsights.trackException({
          error,
          properties: {
            name: `In GenericQuotationTemplate.tsx, error while fetching termsAndCondition document for ${country}.`,
          },
        });
      });
    } else {
      makePdf(appInsights, pdfFileName, lineItems.length, termsAndConditionsDocument);
    }
  };

  const backButtonClick = () => {
    navigate(-1);
  };

  if (isLoading) {
    return <Spinner size={SpinnerSize.large} />;
  }

  return (
    <div className={`generic-quotation-template country-${country.toLowerCase().replace(/\s+/g, '-')}`}>
      {quotationInfo ? (
        <>
          <div className="top30">
            <PrimaryButton className="float-right" onClick={PrintHtmlToPdf}>
              {labels.buttons.generatePdf}
            </PrimaryButton>
            <PrimaryButton className="float-right" onClick={backButtonClick}>
              {labels.buttons.back}
            </PrimaryButton>
          </div>
          <div style={{ padding: "40px 40px" }}>
            <div id={pdfFileName}>
              {/* Header Section */}
              <div className={`flex space-between ${window.innerWidth > 750 ? 'flexDirectionRow' : 'flexDirectionColumn'} width100`}>
                <div style={{ display: "flex", alignItems: "flex-start", gap: country === "Japan" ? "30px" : undefined }}>
                  <div>
                    <div style={{ marginBottom: "20px" }}>
                      <img src="/microsoft-logo.png" className="invlogo" style={{ width: "140px" }} alt="Microsoft logo" />
                    </div>
                    <div>
                      {country === "Japan" ? (
                        <>
                          <p className="pb-1">Microsoft Japan Co., Ltd.</p>
                          <p>Shinagawa Grand Central Tower</p>
                          <p className="pb-1">2-16-3 Konan, Minato-ku</p>
                          <p className="pb-1">Tokyo 108-0075, Japan</p>
                          <p className="pb-1" style={{ marginTop: "10px" }}>
                            <b>TAX ID:</b> T2010401092245
                          </p>
                        </>
                      ) : (
                        <>
                          <p className="pb-1">{countryProfileInfo?.administrativeOriginName}</p>
                          <p>{countryProfileInfo?.administrativeOriginStreet}</p>
                          <p className="pb-1">
                            {countryProfileInfo?.administrativeOriginCity}
                            {countryProfileInfo?.administrativeOriginState ? `, ${countryProfileInfo.administrativeOriginState} ` : " "}
                            {country === "Singapore" ? "" : countryProfileInfo?.administrativeOriginZipCode}
                          </p>
                          <p className="pb-1">
                            {countryProfileInfo?.administrativeOriginCountry}
                            {country === "Singapore" && countryProfileInfo?.administrativeOriginZipCode ? ` ${countryProfileInfo.administrativeOriginZipCode}` : ""}
                          </p>
                          {country === "Singapore" && (
                            <p className="pb-1" style={{ marginTop: "10px" }}>
                              GST Registration Number: M9-0002526-N
                            </p>
                          )}
                        </>
                      )}
                    </div>
                  </div>
                  {country === "Japan" && (
                    <div style={{ paddingTop: "50px" }}>
                      <img
                        src="/Japan-stamp.png"
                        style={{ width: "80px", height: "80px" }}
                        alt="Japan Stamp"
                      />
                    </div>
                  )}
                </div>
                <div style={{ marginTop: "10px" }}>
                  <div>
                    <h1 className="pb-1" style={{ textAlign: "right", fontSize: "24px", fontWeight: "bold" }}>
                      {labels.quotation}
                    </h1>
                  </div>
                  <div>
                    <p><b>{labels.quoteDetails.quotationId}:&nbsp;</b>{quotationInfo?.quotationNumber}</p>
                    <p><b>{labels.quoteDetails.effectiveFrom}:&nbsp;</b>{formattedQuotationDate || quotationInfo?.quotationDate}</p>
                    <p><b>{labels.quoteDetails.effectiveTo}:&nbsp;</b>{formattedExpiryDate || quotationInfo?.expiryDate}</p>
                  </div>
                </div>
              </div>

              {/* Customer Information Section */}
              <div className={`top30 flex space-between ${window.innerWidth > 750 ? 'flexDirectionRow' : 'flexDirectionColumn'}`}>
                <div className={`${window.innerWidth > 750 ? 'width33' : ''}`}>
                  <div className="flexWrap">
                    <p><b>{labels.customerInfo.companyName}:</b> {` ${quotationInfo?.customer?.name}`}</p>
                  </div>
                  <div style={{ display: "flex" }}>
                    <p><b>{labels.customerInfo.accountNumber}:</b>{` ${quotationInfo?.customer?.accountId}`}</p>
                  </div>
                  <div style={{ display: "flex" }}>
                    {quotationInfo?.customer?.region === "EMEA" && (
                      <p>
                        <b>
                          {currentLanguage === "fr" || currentLanguage === "ja"
                            ? labels.customerInfo.customerTaxId
                            : `Customer ${lineItems.length > 0 && lineItems[0]?.taxType !== undefined ? lineItems[0]?.taxType : "Tax"} ID`}:
                        </b>
                        {quotationInfo?.customer?.taxId ? ` ${quotationInfo.customer.taxId}` : ''}
                      </p>
                    )}
                  </div>
                  <div style={{ display: "flex" }}>
                    <p><b>{labels.customerInfo.contactName}:</b>{' '}{customerPrimaryContactName || 'N/A'}</p>
                  </div>
                  <div style={{ display: "flex" }}>
                    <p><b>{labels.customerInfo.contactEmail}:</b>{` ${quotationInfo?.customer?.authorizedEmailId}`}</p>
                  </div>
                  <div style={{ display: "flex" }}>
                    <p><b>{labels.customerInfo.phone}:</b>{` ${quotationInfo?.customer?.authorizedBuyerPhone}`}</p>
                  </div>
                  <div style={{ display: "flex", marginTop: "30px" }}>
                    <p style={{ whiteSpace: "nowrap" }}>
                      <b>{labels.microsoftAccount.accountLead}:</b>{` ${quotationInfo?.microsoftAccountLead}`}
                    </p>
                  </div>
                  <div style={{ display: "flex" }}>
                    <p style={{ whiteSpace: "nowrap" }}>
                      <b>{labels.microsoftAccount.accountLeadEmail}:</b>{` ${quotationInfo?.microsoftAccountEmail}`}
                    </p>
                  </div>
                  <div style={{ display: "flex" }}>
                    <p><b>{labels.customerInfo.opportunityId}:</b>{` ${quotationInfo?.msxOpportunityId}`}</p>
                  </div>
                  <div style={{ display: "flex" }}>
                    <p><b>{labels.customerInfo.customerPo}:</b>{` ${quotationInfo?.customerPurchaseOrder || ""}`}</p>
                  </div>
                </div>

                {/* Address Information */}
                <div className={`${window.innerWidth > 750 ? 'width33' : ''}  ${window.innerWidth < 750 ? 'top20' : '0'}`}>
                  <table className="table-no-border">
                    <tbody>
                      <tr>
                        <td className="td-border">
                          <p><strong>{labels.addresses.billTo}:</strong></p>
                          <p>{quotationInfo?.customer?.selectedBillToAddress?.name}</p>
                          <p>{quotationInfo?.customer?.selectedBillToAddress?.street}</p>
                          <p>{`${quotationInfo?.customer?.selectedBillToAddress?.city || ''}${quotationInfo?.customer?.selectedBillToAddress?.state ? ', ' + quotationInfo.customer.selectedBillToAddress.state : ''}${quotationInfo?.customer?.selectedBillToAddress?.province ? ', ' + quotationInfo.customer.selectedBillToAddress.province : ''}${quotationInfo?.customer?.selectedBillToAddress?.country === "Singapore" ? '' : ' ' + (quotationInfo?.customer?.selectedBillToAddress?.postalCode || '')}`.trim()}</p>
                          <p>{quotationInfo?.customer?.selectedBillToAddress?.country}{quotationInfo?.customer?.selectedBillToAddress?.country === "Singapore" ? ` ${quotationInfo?.customer?.selectedBillToAddress?.postalCode || ''}` : ''}</p>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
                <div className={`${window.innerWidth > 750 ? 'width33' : ''}  ${window.innerWidth < 750 ? 'top20' : '0'}`}>
                  <table className="table-no-border">
                    <tbody>
                      <tr>
                        <td className="td-border">
                          <p><strong>{labels.addresses.shipTo}:</strong></p>
                          <p>{quotationInfo?.customer?.selectedShipToAddress?.name}</p>
                          <p>{quotationInfo?.customer?.selectedShipToAddress?.street}</p>
                          <p>{`${quotationInfo?.customer?.selectedShipToAddress?.city || ''}${quotationInfo?.customer?.selectedShipToAddress?.state ? ', ' + quotationInfo.customer.selectedShipToAddress.state : ''}${quotationInfo?.customer?.selectedShipToAddress?.province ? ', ' + quotationInfo.customer.selectedShipToAddress.province : ''}${quotationInfo?.customer?.selectedShipToAddress?.country === "Singapore" ? '' : ' ' + (quotationInfo?.customer?.selectedShipToAddress?.postalCode || '')}`.trim()}</p>
                          <p>{quotationInfo?.customer?.selectedShipToAddress?.country}{quotationInfo?.customer?.selectedShipToAddress?.country === "Singapore" ? ` ${quotationInfo?.customer?.selectedShipToAddress?.postalCode || ''}` : ''}</p>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Comments Section */}
              {quotationInfo?.comments && (
                <div style={{ borderTop: "1px solid #c4c4c4", marginTop: "10px", alignContent: "center" }}>
                  <p>
                    <strong style={{ marginRight: "5px" }}>
                      Comments:
                    </strong>
                    {quotationInfo?.comments}
                  </p>
                </div>
              )}

              {/* Product Table */}
              <div className="row top20 overFlowXAuto">
                <table className="table">
                  <tr className="background-style">
                    <th className="table-border">{labels.productTable.description}</th>
                    <th className="table-border">{labels.productTable.partNumber}</th>
                    <th className="table-border">{labels.productTable.quantity}</th>
                    {lineItems[0]?.taxType ? (
                      <th className="table-border">{labels.productTable.Price}</th>
                    ) : (
                      <th className="table-border">MSRP</th>
                    )}
                    <th className="table-border">{labels.productTable.discountAmount}</th>
                    <th className="table-border">{labels.productTable.discountPercentage}</th>
                    <th className="table-border">{labels.productTable.unitPrice}</th>
                    {quotationInfo?.customer?.region?.toUpperCase() === "APAC" && (
                      <th className="table-border">Extended Discount Amount</th>
                    )}
                    {quotationInfo?.customer?.region === "EMEA" && (
                      <th className="table-border">
                        {labels.productTable.fee}{quotationInfo?.totalFee && Number(quotationInfo.totalFee) > 0 ? '*' : ''}
                      </th>
                    )}
                    {quotationInfo?.customer?.region !== "APAC" && (
                      <th className="table-border">
                        {currentLanguage === "fr" || currentLanguage === "ja"
                          ? labels.productTable.tax
                          : `Extended Line ${lineItems[0]?.taxType !== null ? lineItems[0]?.taxType : 'Tax'}`}
                      </th>
                    )}
                    <th className="table-border" style={{ textAlign: "center" }}>
                      <p>{labels.productTable.lineTotal}</p>
                    </th>
                  </tr>
                  {lineItems?.map((product, index) => (
                    <tr key={index}>
                      <td className="table-border">{product.productTitle}</td>
                      <td className="table-border">{product.sapItemId}</td>
                      <td className="table-border">{product.quantity}</td>
                      <td className="text-right table-border">
                        {country === "Japan" ? formattedPriceForJapan(product?.msrp) : formattedPrice(product?.msrp)}
                      </td>
                      <td className="text-right table-border">
                        {country === "Japan" ? formattedPriceForJapan((product?.discountedPrice) / product.quantity) : formattedPrice((product?.discountedPrice) / product.quantity)}
                      </td>
                      <td className="text-right table-border">
                        {product?.discountedPercentage}%
                      </td>
                      <td className="text-right table-border">
                        {country === "Japan" ? formattedPriceForJapan(product?.unitPrice) : formattedPrice(product?.unitPrice)}
                      </td>
                      {quotationInfo?.customer?.region?.toUpperCase() === "APAC" && (
                        <td className="text-right table-border">
                          {country === "Japan" ? formattedPriceForJapan(product?.discountedPrice) : formattedPrice(product?.discountedPrice)}
                        </td>
                      )}
                      {quotationInfo?.customer?.region === "EMEA" && (
                        <td className="text-right table-border">
                          {country === "Japan" ? formattedPriceForJapan(product?.totalLineLevyFeeAmount) : formattedPrice(product?.totalLineLevyFeeAmount)}
                        </td>
                      )}
                      {quotationInfo?.customer?.region !== "APAC" && (
                        <td className="text-right table-border">
                          {country === "Japan" ? formattedPriceForJapan(product?.lineItemTax) : formattedPrice(product?.lineItemTax)}
                        </td>
                      )}
                      <td className="text-right table-border">
                        {country === "Japan" ? formattedPriceForJapan(product?.lineTotal) : formattedPrice(product?.lineTotal)}
                      </td>
                    </tr>
                  ))}
                </table>
              </div>

              {/* Summary Section */}
              <div className="below-table">
                <div className="">
                  <tr>
                    <td colSpan={columnsNumber} className="tDataWithoutBorder">
                      <strong>{labels.summary.shipping}</strong>
                    </td>
                    <td colSpan={1} className="text-right tDataWithoutBorder">
                      {country === "Japan" ? priceWithCurrencyForJapan(quotationInfo?.quoteCurrency, 0) : priceWithCurrency(quotationInfo?.quoteCurrency, 0)}
                    </td>
                  </tr>
                  {quotationInfo?.customer?.region === "EMEA" && (
                    <tr>
                      <td colSpan={columnsNumber} className="tDataWithoutBorder">
                        <strong>
                          {labels.summary.totalFee}
                        </strong>
                      </td>
                      <td colSpan={1} className="text-right tDataWithoutBorder">
                        {country === "Japan" ? priceWithCurrencyForJapan(
                          quotationInfo?.quoteCurrency,
                          quotationInfo?.totalFee,
                        ) : priceWithCurrency(
                          quotationInfo?.quoteCurrency,
                          quotationInfo?.totalFee
                        )}
                      </td>
                    </tr>
                  )}
                  <tr>
                    <td colSpan={columnsNumber} className="tDataWithoutBorder">
                      <strong>
                        {["Japan", "New Zealand", "Singapore"].includes(country)
                          ? labels.summary.subtotal
                          : labels.summary.subtotalWithLevy}
                      </strong>
                    </td>
                    <td colSpan={1} className="text-right tDataWithoutBorder">
                      {country === "Japan" ? priceWithCurrencyForJapan(
                        quotationInfo?.quoteCurrency,
                        quotationInfo?.subTotal,
                      ) : priceWithCurrency(
                        quotationInfo?.quoteCurrency,
                        quotationInfo?.subTotal
                      )}
                    </td>
                  </tr>
                  <tr>
                    <td colSpan={columnsNumber} className="tDataWithoutBorder">
                      <strong>
                        {currentLanguage === "fr" || currentLanguage === "ja"
                          ? labels.summary.totalTax
                          : `${lineItems[0]?.taxType !== null ? lineItems[0]?.taxType : 'Tax'} Amount (${lineItems[0]?.taxType !== null ? lineItems[0]?.taxType : 'Tax'} Rate)`}
                      </strong>
                    </td>
                    <td colSpan={1} className="text-right tDataWithoutBorder">
                      {country === "Japan" ? priceWithCurrencyForJapan(
                        quotationInfo?.quoteCurrency,
                        (quotationInfo?.totalTax) ? quotationInfo?.totalTax : 0,
                      ) : priceWithCurrency(
                        quotationInfo?.quoteCurrency,
                        (quotationInfo?.totalTax) ? quotationInfo?.totalTax : 0
                      )}
                      {' '}
                      (
                      {(lineItems.find(item => item?.effectiveTaxRate > 0)?.effectiveTaxRate || 0) * 100}
                      %){ }
                    </td>
                  </tr>
                  <tr>
                    <td colSpan={columnsNumber} className="tDataWithoutBorder">
                      <strong>
                        {labels.summary.grandTotal}{country === "Singapore" ?  " (inclusive of GST)" : ""}
                      </strong>
                    </td>
                    <td colSpan={1} className="text-right tDataWithoutBorder">
                      {country === "Japan" ? priceWithCurrencyForJapan(
                        quotationInfo?.quoteCurrency,
                        quotationInfo?.total,
                      ) : priceWithCurrency(
                        quotationInfo?.quoteCurrency,
                        quotationInfo?.total
                      )}
                    </td>
                  </tr>
                  <tr>
                    <td colSpan={columnsNumber} className="tDataWithoutBorder">
                      <strong>{labels.summary.currency}</strong>
                    </td>
                    <td colSpan={1} className="text-right tDataWithoutBorder">
                      {`${quotationInfo.quoteCurrency} ${symbolForCurrency(quotationInfo.quoteCurrency)}`}
                    </td>
                  </tr>
                </div>
              </div>

              {/* Footer */}
              <div className={`top60 flex space-between ${window.innerWidth > 750 ? 'flexDirectionRow' : 'flexDirectionColumn'}`}>
                <strong>
                  {labels.footer.customerSignature} __________________________
                </strong>
                <span style={{ paddingRight: "10px" }}>
                  <strong style={{ paddingRight: "10px" }}>{labels.footer.date}:</strong>
                  {formattedCurrentDate || new Date().toLocaleDateString('en-US')}
                </span>
              </div>
              <div style={{ marginTop: "20px", paddingBottom: "50px" }}>
                <p>
                  {labels.footer.termsNote}
                </p>
                {quotationInfo?.customer?.region === "EMEA" && Number(quotationInfo?.totalFee) > 0 ? (
                  <p style={{ marginTop: "15px" }}>
                    {labels.footer.levyFeeExplanation}
                  </p>
                ) : null}
                {country === "France" && quotationInfo?.totalFee && Number(quotationInfo.totalFee) > 0 ? (
                  <div style={{ marginTop: "15px" }}>
                    <p>{labels.footer.copyrightLevyFeeLabel}: {priceWithCurrency(quotationInfo.quoteCurrency, quotationInfo.totalFee)}</p>
                    <p>{labels.footer.officialNoticeLabel}: <a href="https://www.culture.gouv.fr/nous-connaitre/organisation-du-ministere/commission-pour-la-remuneration-de-la-copie-privee" target="_blank" rel="noopener noreferrer">https://www.culture.gouv.fr/nous-connaitre/organisation-du-ministere/commission-pour-la-remuneration-de-la-copie-privee</a></p>
                    <p>{labels.footer.reimbursementLabel}: <a href="https://copiefrance.fr/fr/" target="_blank" rel="noopener noreferrer">https://copiefrance.fr/fr/</a></p>
                  </div>
                ) : null}
                {country === "New Zealand" && (
                  <p style={{ marginTop: "15px", fontSize: "12px" }}>
                    <strong>To learn more, click here (<a href="https://www.microsoft.com/en-nz/store/b/business" target="_blank" rel="noopener noreferrer">https://www.microsoft.com/en-nz/store/b/business</a>)</strong>
                  </p>
                )}
                {country === "Singapore" && (
                  <p style={{ marginTop: "15px", fontSize: "12px" }}>
                    <strong>To learn more, click here (<a href="https://www.microsoft.com/en-sg/store/b/business" target="_blank" rel="noopener noreferrer">https://www.microsoft.com/en-sg/store/b/business</a>)</strong>
                  </p>
                )}
                {country === "Austria" && hasWarrantyItems(lineItems) && (
                  <p style={{ marginTop: "15px" }}>
                    <strong>Austria Electronic (E)Consent:</strong> I hereby expressly give my consent to receive documentation and communications associated with the Microsoft Protection Plan through electronic means. I can at any time revoke such consent or provide another e-mail address to be used. Likewise, the insurer has the right to revoke its consent to e-communication. Both parties to the insurance contract still have the right to send declarations, communications and information on paper. I have the right to ask for hardcopies of any declarations or information received electronically once free of charge.
                  </p>
                )}
                {country === "Italy" && hasWarrantyItems(lineItems) && (
                  <p style={{ marginTop: "15px" }}>
                    <strong>Italy Terms & Conditions (T&C) Consent:</strong> "I confirm that I have read, and I agree to the Terms and Conditions of the Microsoft Protection Plan".
                  </p>
                )}
                {/* Domestic reverse charges message for specific countries */}
                {showDomesticReverseCharges && (
                  <p style={{ marginTop: "15px" }}>
                    *Domestic reverse charges applied
                  </p>
                )}
              </div>
            </div>
          </div>
        </>
      ) : (
        <Spinner size={SpinnerSize.large} />
      )}
    </div>
  );
};
